import React, { useMemo } from 'react';
import { useForm } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';

import { JSONSchema, JSONSchemaProperty, orderSchemaFields } from '../types';

import styles from './SchemaForm.module.scss';

interface SchemaFormProps {
  schema: JSONSchema;
  /** Admin-configured render order; `null`/omitted keeps the schema's order. */
  fieldOrder?: string[] | null;
  onSubmit: (values: Record<string, unknown>) => void;
  isSubmitting: boolean;
  serverErrors?: Record<string, string>;
  defaultValues?: Record<string, unknown>;
  submitLabel?: string;
}

function humanizeKey(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase())
    .trim();
}

function getInitialValues(
  schema: JSONSchema,
  defaultValues?: Record<string, unknown>,
): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  const properties = schema.properties || {};

  for (const [key, prop] of Object.entries(properties)) {
    if (defaultValues && key in defaultValues) {
      values[key] = defaultValues[key];
    } else if (prop.default !== undefined) {
      values[key] = prop.default;
    } else {
      const fieldType = Array.isArray(prop.type) ? prop.type[0] : prop.type;
      switch (fieldType) {
        case 'boolean':
          values[key] = false;
          break;
        case 'number':
        case 'integer':
          values[key] = '';
          break;
        case 'array':
          values[key] = [];
          break;
        case 'object':
          values[key] = '';
          break;
        default:
          values[key] = '';
      }
    }
  }

  return values;
}

function getFieldType(prop: JSONSchemaProperty): string {
  const type = Array.isArray(prop.type) ? prop.type[0] : prop.type;

  if (type === 'boolean') return 'checkbox';
  if (type === 'number' || type === 'integer') return 'number';

  if (type === 'string') {
    if (prop.enum && prop.enum.length > 0) return 'select';
    if (prop.format === 'date') return 'date';
    if (prop.format === 'date-time') return 'datetime-local';
    if (prop.format === 'email') return 'email';
    if (prop.format === 'uri') return 'url';
    if (
      (prop.maxLength && prop.maxLength > 100) ||
      (prop.minLength && prop.minLength > 100)
    )
      return 'textarea';
    return 'text';
  }

  if (type === 'array') {
    if (prop.items?.enum) return 'multiselect';
    const itemType = Array.isArray(prop.items?.type)
      ? prop.items?.type[0]
      : prop.items?.type;
    if (itemType === undefined || itemType === 'string') return 'array-textarea';
    return 'json';
  }

  if (type === 'object') return 'json';

  return 'text';
}

interface ArrayTextareaFieldProps {
  fieldKey: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  field: any;
  error: string | undefined;
  placeholder: string;
  hint: string;
  styles: { readonly [key: string]: string };
}

const ArrayTextareaField: React.FC<ArrayTextareaFieldProps> = ({
  fieldKey,
  field,
  error,
  placeholder,
  hint,
  styles,
}) => {
  const valueFromForm: string[] = Array.isArray(field.state.value)
    ? (field.state.value as unknown[]).map((v) => String(v))
    : [];
  // Keep a local raw text so the user's intermediate state (e.g. trailing
  // newline while typing the next item) isn't clobbered by the parsed array
  // round-trip.
  const [rawText, setRawText] = React.useState<string>(valueFromForm.join('\n'));

  // Re-sync local text if the form value changes from outside (e.g. reset /
  // defaultValues). We only sync when the parsed form value diverges from what
  // our raw text would parse to, to avoid stomping on the user's caret.
  const parsedFromRaw = rawText
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  const formStr = valueFromForm.join('\n');
  const rawParsedStr = parsedFromRaw.join('\n');
  React.useEffect(() => {
    if (formStr !== rawParsedStr) {
      setRawText(formStr);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formStr]);

  return (
    <>
      <textarea
        id={fieldKey}
        className={styles.textarea}
        value={rawText}
        onChange={(e) => {
          const raw = e.target.value;
          setRawText(raw);
          const parsed = raw
            .split('\n')
            .map((line) => line.trim())
            .filter((line) => line.length > 0);
          field.handleChange(parsed);
        }}
        onBlur={field.handleBlur}
        rows={5}
        placeholder={placeholder}
      />
      <p className={styles.hint}>{hint}</p>
      {error && <p className={styles.error}>{error}</p>}
    </>
  );
};

interface FieldRendererProps {
  fieldKey: string;
  prop: JSONSchemaProperty;
  isRequired: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: any;
  serverError?: string;
}

const FieldRenderer: React.FC<FieldRendererProps> = ({
  fieldKey,
  prop,
  isRequired,
  form,
  serverError,
}) => {
  const { t } = useTranslation();
  const label = prop.title || humanizeKey(fieldKey);
  const hint = prop.description;
  const fieldType = getFieldType(prop);

  return (
    <div className={styles.field}>
      {fieldType !== 'checkbox' && (
        <label className={styles.label} htmlFor={fieldKey}>
          {label}
          {isRequired && <span className={styles.required}>*</span>}
        </label>
      )}
      {hint && <p className={styles.hint}>{hint}</p>}

      <form.Field
        name={fieldKey}
        validators={{
          onSubmit: ({ value }: { value: unknown }) => {
            if (isRequired && (value === '' || value === null || value === undefined)) {
              return t('This field is required');
            }
            if (fieldType === 'number' && value !== '' && value !== undefined) {
              const num = Number(value);
              if (isNaN(num)) return t('Must be a number');
              if (prop.minimum !== undefined && num < prop.minimum)
                return `${t('Minimum')}: ${prop.minimum}`;
              if (prop.maximum !== undefined && num > prop.maximum)
                return `${t('Maximum')}: ${prop.maximum}`;
            }
            if (
              fieldType === 'text' &&
              typeof value === 'string' &&
              prop.pattern
            ) {
              // Schema-supplied patterns can be invalid regex (esp. when authored
              // for a different regex flavour). Skip validation rather than crash
              // or surface a misleading "Invalid format" error.
              try {
                const re = new RegExp(prop.pattern);
                if (!re.test(value)) return t('Invalid format');
              } catch {
                // ignore invalid pattern
              }
            }
            return undefined;
          },
        }}
      >
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        {(field: any) => {
          const error = field.state.meta.errors?.[0] || serverError;

          if (fieldType === 'checkbox') {
            return (
              <div className={styles.checkboxWrapper}>
                <input
                  id={fieldKey}
                  type="checkbox"
                  checked={!!field.state.value}
                  onChange={(e) => field.handleChange(e.target.checked)}
                  onBlur={field.handleBlur}
                />
                <label htmlFor={fieldKey} className={styles.checkboxLabel}>
                  {label}
                  {isRequired && <span className={styles.required}>*</span>}
                </label>
                {error && <p className={styles.error}>{error}</p>}
              </div>
            );
          }

          if (fieldType === 'select') {
            return (
              <>
                <select
                  id={fieldKey}
                  className={styles.input}
                  value={String(field.state.value ?? '')}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                >
                  <option value="">{t('Select...')}</option>
                  {(prop.enum || []).map((opt) => (
                    <option key={String(opt)} value={String(opt)}>
                      {String(opt)}
                    </option>
                  ))}
                </select>
                {error && <p className={styles.error}>{error}</p>}
              </>
            );
          }

          if (fieldType === 'multiselect') {
            const selected = Array.isArray(field.state.value)
              ? (field.state.value as unknown[])
              : [];
            return (
              <>
                <div className={styles.multiselectGroup}>
                  {(prop.items?.enum || []).map((opt) => {
                    const val = String(opt);
                    const checked = selected.includes(opt);
                    return (
                      <label key={val} className={styles.checkboxLabel}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const next = checked
                              ? selected.filter((v) => v !== opt)
                              : [...selected, opt];
                            field.handleChange(next);
                          }}
                        />
                        {val}
                      </label>
                    );
                  })}
                </div>
                {error && <p className={styles.error}>{error}</p>}
              </>
            );
          }

          if (fieldType === 'array-textarea') {
            return (
              <ArrayTextareaField
                fieldKey={fieldKey}
                field={field}
                error={error}
                placeholder={t('Enter one value per line') as string}
                hint={t('Enter one value per line') as string}
                styles={styles}
              />
            );
          }

          if (fieldType === 'textarea' || fieldType === 'json') {
            return (
              <>
                <textarea
                  id={fieldKey}
                  className={styles.textarea}
                  value={
                    typeof field.state.value === 'string'
                      ? field.state.value
                      : JSON.stringify(field.state.value, null, 2)
                  }
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  rows={5}
                  placeholder={
                    fieldType === 'json' ? t('Enter JSON') : undefined
                  }
                />
                {error && <p className={styles.error}>{error}</p>}
              </>
            );
          }

          return (
            <>
              <input
                id={fieldKey}
                type={fieldType}
                className={styles.input}
                value={String(field.state.value ?? '')}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                min={prop.minimum}
                max={prop.maximum}
              />
              {error && <p className={styles.error}>{error}</p>}
            </>
          );
        }}
      </form.Field>
    </div>
  );
};

export const SchemaForm: React.FC<SchemaFormProps> = ({
  schema,
  fieldOrder,
  onSubmit,
  isSubmitting,
  serverErrors,
  defaultValues,
  submitLabel,
}) => {
  const { t } = useTranslation();
  const initialValues = useMemo(
    () => getInitialValues(schema, defaultValues),
    [schema, defaultValues],
  );

  const properties = schema.properties || {};
  const requiredFields = new Set(schema.required || []);

  const form = useForm({
    defaultValues: initialValues,
    onSubmit: async ({ value }) => {
      // Convert number fields
      const processed: Record<string, unknown> = {};
      for (const [key, val] of Object.entries(value)) {
        const prop = properties[key];
        if (!prop) {
          processed[key] = val;
          continue;
        }
        const pType = Array.isArray(prop.type) ? prop.type[0] : prop.type;
        if (
          (pType === 'number' || pType === 'integer') &&
          typeof val === 'string' &&
          val !== ''
        ) {
          processed[key] = pType === 'integer' ? parseInt(val, 10) : parseFloat(val);
        } else if (getFieldType(prop) === 'json' && typeof val === 'string') {
          try {
            processed[key] = JSON.parse(val);
          } catch {
            processed[key] = val;
          }
        } else {
          processed[key] = val;
        }
      }
      onSubmit(processed);
    },
  });

  const fieldKeys = orderSchemaFields(properties, fieldOrder);

  if (fieldKeys.length === 0) {
    return null;
  }

  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        form.handleSubmit();
      }}
    >
      {fieldKeys.map((key) => (
        <FieldRenderer
          key={key}
          fieldKey={key}
          prop={properties[key]}
          isRequired={requiredFields.has(key)}
          form={form}
          serverError={serverErrors?.[key]}
        />
      ))}

      <button
        type="submit"
        className={styles.submitButton}
        disabled={isSubmitting}
      >
        {isSubmitting ? t('Running...') : submitLabel || t('Run')}
      </button>
    </form>
  );
};
